import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  approveSceneImage,
  chooseSceneImage,
  generateSceneImage,
  listSceneImages,
} from '@platform/client';
import {
  sceneArtName,
  sceneArtUrl,
  sceneImageId,
  sceneImagesSchema,
  sceneImageUrlIn,
  sceneImageVersionSchema,
  sceneSizedArt,
} from '@platform/schema/scene-image';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';

import type { GenerateSceneImageData } from '@platform/client';
import type { SceneImageVersion } from '@platform/schema/scene-image';

type GenerateSceneImage = GenerateSceneImageData['body'];

/** Preview widths, in CSS pixels. A version is shown twice, light beside dark. */
const PREVIEW = 240;
const THUMB = 120;

const svgSource = (svg: string) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

function Picture({
  src,
  label,
  width,
}: {
  src: string;
  label: string;
  width: number;
}) {
  return (
    <Stack spacing={0.5}>
      <img src={src} alt={label} width={width} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

/** Light beside dark, because approving puts it in front of learners in both at once. */
function BothSchemes({ light, dark }: { light: string; dark: string }) {
  return (
    <Stack direction="row" spacing={2}>
      <Picture src={light} label="Light" width={PREVIEW} />
      <Picture src={dark} label="Dark" width={PREVIEW} />
    </Stack>
  );
}

function Current({
  imageUrl,
  alt,
}: {
  imageUrl: string | undefined;
  alt: string;
}) {
  if (imageUrl === undefined) {
    return (
      <Typography variant="body2" color="text.secondary">
        No image. The scene shows the lesson’s object.
      </Typography>
    );
  }
  if (sceneImageId(imageUrl) !== undefined) {
    return (
      <BothSchemes
        light={sceneImageUrlIn(imageUrl, 'light')}
        dark={sceneImageUrlIn(imageUrl, 'dark')}
      />
    );
  }
  const art = sceneArtName(imageUrl);
  // A drawn picture is named by the scene it shows — GEN-231; a catalog one by its name.
  return art === undefined ? (
    <img src={imageUrl} alt={alt} width={PREVIEW} />
  ) : (
    <Picture src={imageUrl} label={art} width={PREVIEW} />
  );
}

/** The lesson's drawn cover — GEN-231. Drawn by a build; nothing here makes one. */
export function LessonCover({
  url,
  title,
}: {
  url: string | undefined;
  title: string;
}) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="subtitle2">Cover</Typography>
      {url === undefined ? (
        <Typography variant="body2" color="text.secondary">
          No cover drawn yet.
        </Typography>
      ) : (
        <img src={url} alt={title} width={PREVIEW} />
      )}
    </Stack>
  );
}

/**
 * A scene's picture — VIS-17. Attach one from the drawn catalog, or generate one,
 * refine it and approve it; either shows in the app at once. Only a reading scene without
 * a figure has one, since those are the only scenes the app draws an image on.
 */
export function SceneImage({
  sceneId,
  imageUrl,
  alt,
  hasFigure,
  question = false,
  disabled = false,
  onChanged,
}: {
  sceneId: string;
  imageUrl: string | undefined;
  /** The scene's headline, which a drawn picture is announced by. */
  alt: string;
  hasFigure: boolean;
  /** A question scene: it shows the picture its build drew and offers nothing to change. */
  question?: boolean;
  disabled?: boolean;
  /** Re-read the lesson, which is what moves the preview. */
  onChanged: () => void;
}) {
  const queries = useQueryClient();
  const [picking, setPicking] = useState(false);
  const [generating, setGenerating] = useState(false);

  const choose = useTrackedMutation('Change the image', {
    mutationFn: async (next: string | null) => {
      await chooseSceneImage({
        path: { id: sceneId },
        body: { imageUrl: next },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      setPicking(false);
      onChanged();
    },
  });

  // A build draws a question or figure scene a picture too, and this shows it — GEN-231.
  if (question || (hasFigure && imageUrl !== undefined)) {
    return (
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Image</Typography>
        <Current imageUrl={imageUrl} alt={alt} />
      </Stack>
    );
  }

  if (hasFigure) {
    return (
      <Alert severity="info">
        This scene shows its figure, so learners see no image here.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Image</Typography>
      <Current imageUrl={imageUrl} alt={alt} />
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          disabled={disabled}
          onClick={() => {
            setPicking(true);
          }}
        >
          Choose from catalog
        </Button>
        <Button
          size="small"
          disabled={disabled}
          onClick={() => {
            setGenerating((open) => !open);
          }}
        >
          {generating ? 'Close generator' : 'Generate'}
        </Button>
        {imageUrl !== undefined && (
          <Button
            size="small"
            color="error"
            disabled={disabled || choose.isPending}
            onClick={() => {
              choose.mutate(null);
            }}
          >
            Remove image
          </Button>
        )}
      </Stack>
      {choose.isError && (
        <Alert severity="error">The image could not be changed.</Alert>
      )}

      <Dialog
        open={picking}
        onClose={() => {
          setPicking(false);
        }}
        maxWidth="md"
      >
        <DialogTitle>Choose a catalog image</DialogTitle>
        <DialogContent>
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            sx={{ flexWrap: 'wrap' }}
          >
            {sceneSizedArt.map((name) => (
              <ButtonBase
                key={name}
                aria-label={name}
                disabled={choose.isPending}
                onClick={() => {
                  choose.mutate(sceneArtUrl(name));
                }}
              >
                <Picture src={sceneArtUrl(name)} label={name} width={PREVIEW} />
              </ButtonBase>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>

      {generating && (
        <Generator
          sceneId={sceneId}
          onApproved={() => {
            void queries.invalidateQueries({
              queryKey: ['scene-images', sceneId],
            });
            onChanged();
          }}
        />
      )}
    </Stack>
  );
}

function Generator({
  sceneId,
  onApproved,
}: {
  sceneId: string;
  onApproved: () => void;
}) {
  const queries = useQueryClient();
  const versions = useQuery({
    queryKey: ['scene-images', sceneId],
    // Parsed rather than cast: the SDK types a nullable field as `unknown`.
    queryFn: async () =>
      sceneImagesSchema.parse(
        (await listSceneImages({ path: { id: sceneId }, throwOnError: true }))
          .data,
      ),
  });
  const [prompt, setPrompt] = useState<string | undefined>(undefined);
  const [feedback, setFeedback] = useState('');
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [last, setLast] = useState<GenerateSceneImage | undefined>(undefined);

  const generate = useTrackedMutation('Generate an image', {
    mutationFn: async (body: GenerateSceneImage) => {
      setLast(body);
      return sceneImageVersionSchema.parse(
        (
          await generateSceneImage({
            path: { id: sceneId },
            body,
            throwOnError: true,
          })
        ).data,
      );
    },
    onSuccess: async (version) => {
      setSelectedId(version.id);
      setFeedback('');
      await queries.invalidateQueries({ queryKey: ['scene-images', sceneId] });
    },
  });

  const approve = useTrackedMutation('Approve the image', {
    mutationFn: async (id: string) => {
      await approveSceneImage({ path: { id }, throwOnError: true });
    },
    onSuccess: onApproved,
  });

  if (versions.data === undefined) {
    return versions.isError ? (
      <Alert severity="error">The versions could not be loaded.</Alert>
    ) : (
      <Typography variant="body2">Loading versions…</Typography>
    );
  }

  const written = prompt ?? versions.data.suggestedPrompt;
  const all = versions.data.versions;
  const selected: SceneImageVersion | undefined =
    all.find((version) => version.id === selectedId) ?? all[0];
  const busy = generate.isPending || approve.isPending;

  return (
    <Stack spacing={1.5}>
      <TextField
        label="Prompt"
        multiline
        minRows={2}
        value={written}
        onChange={(event) => {
          setPrompt(event.target.value);
        }}
      />
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          size="small"
          disabled={busy || written.trim().length === 0}
          onClick={() => {
            generate.mutate({ prompt: written });
          }}
        >
          Generate
        </Button>
        {selected !== undefined && (
          <Button
            size="small"
            disabled={busy}
            onClick={() => {
              generate.mutate({ prompt: written, basedOnId: selected.id });
            }}
          >
            Regenerate
          </Button>
        )}
      </Stack>
      {generate.isPending && (
        <Typography variant="body2">
          Drawing… this takes a minute or two.
        </Typography>
      )}
      {generate.isError && (
        <Alert
          severity="error"
          action={
            last !== undefined && (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  generate.mutate(last);
                }}
              >
                Try again
              </Button>
            )
          }
        >
          The image could not be made. Nothing on the scene changed.
        </Alert>
      )}

      {selected !== undefined && (
        <Stack spacing={1.5}>
          <BothSchemes
            light={svgSource(selected.light)}
            dark={svgSource(selected.dark)}
          />
          {selected.refusal !== null && (
            <Alert severity="warning">
              This version {selected.refusal}, so it cannot be approved. Refine
              it or restore an earlier one.
            </Alert>
          )}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <TextField
              label="Feedback"
              size="small"
              placeholder="warmer, no chart"
              value={feedback}
              onChange={(event) => {
                setFeedback(event.target.value);
              }}
              sx={{ flex: 1 }}
            />
            <Button
              size="small"
              disabled={busy || feedback.trim().length === 0}
              onClick={() => {
                generate.mutate({
                  prompt: written,
                  feedback,
                  basedOnId: selected.id,
                });
              }}
            >
              Refine
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={busy || selected.refusal !== null}
              onClick={() => {
                approve.mutate(selected.id);
              }}
            >
              Approve
            </Button>
          </Stack>
          {approve.isError && (
            <Alert severity="error">The image could not be approved.</Alert>
          )}
        </Stack>
      )}

      {all.length > 1 && (
        <Stack spacing={1}>
          <Typography variant="subtitle2">Versions</Typography>
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            sx={{ flexWrap: 'wrap' }}
          >
            {all.map((version, index) => (
              <Stack key={version.id} spacing={0.5}>
                <Picture
                  src={svgSource(version.light)}
                  label={`Version ${String(all.length - index)}`}
                  width={THUMB}
                />
                <Stack direction="row" spacing={0.5}>
                  {version.approved && <Chip size="small" label="Approved" />}
                  {version.refusal !== null && (
                    <Chip size="small" label="Refused" />
                  )}
                </Stack>
                <Button
                  size="small"
                  disabled={version.id === selected?.id}
                  onClick={() => {
                    setSelectedId(version.id);
                  }}
                >
                  {version.id === selected?.id ? 'Showing' : 'Restore'}
                </Button>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}
